import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Pencil, Plus, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { horseAPI, crmAPI } from '../../../services/api';
import api from '../../../services/api';

const HorseBoardingCard = ({
  horse, canEditAccount, onUpdate,
  boardingFacilities, trainerFacilities, billingEntities,
  isFarm, onEntitiesInvalidate,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [farmfolioLocations, setFarmfolioLocations] = useState([]);

  // Create dialogs
  const [createBoardingOpen, setCreateBoardingOpen] = useState(false);
  const [createTrainerOpen, setCreateTrainerOpen]   = useState(false);
  const [newBoardingName, setNewBoardingName]        = useState('');
  const [newTrainerFirst, setNewTrainerFirst]        = useState('');
  const [newTrainerLast, setNewTrainerLast]          = useState('');

  const startEditing = () => {
    setForm({
      boarding_type:             horse.boarding_type             || 'boarding_farm',
      boarding_location_id:      horse.boarding_location_id      || '',
      default_billing_entity_id: horse.default_billing_entity_id || '',
      boarding_notes:            horse.boarding_notes            || '',
      farmfolio_location_id:     horse.farmfolio_location_id     || '',
      farmfolio_location_name:   horse.farmfolio_location_name   || '',
    });
    setIsEditing(true);
    if (isFarm) {
      api.get('/farmfolio/locations').then(r => setFarmfolioLocations(r.data || [])).catch(() => {});
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const isLinked = form.boarding_type === 'boarding_farm' || form.boarding_type === 'with_trainer';
      await horseAPI.update(horse.id, {
        boarding_type:             form.boarding_type,
        boarding_location_id:      isLinked ? (form.boarding_location_id || null) : null,
        boarding_notes:            form.boarding_type === 'other'   ? (form.boarding_notes || null) : null,
        farmfolio_location_id:     form.boarding_type === 'on_farm' ? (form.farmfolio_location_id || null) : null,
        farmfolio_location_name:   form.boarding_type === 'on_farm' ? (form.farmfolio_location_name || null) : null,
        default_billing_entity_id: form.default_billing_entity_id || null,
      });
      toast.success('Boarding location updated');
      setIsEditing(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBoarding = async () => {
    if (!newBoardingName.trim()) { toast.error('Please enter a facility name'); return; }
    setSaving(true);
    try {
      const response = await crmAPI.create({ crm_class: 'contact', crm_type: 'organization', system_name: newBoardingName, organization_name: newBoardingName, organization_industry_role: 'farm_boarding' });
      const newId = response.data?.id || response.id;
      toast.success('Boarding facility created');
      setCreateBoardingOpen(false); setNewBoardingName('');
      onEntitiesInvalidate?.();
      setForm(prev => ({ ...prev, boarding_location_id: newId }));
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(Array.isArray(msg) ? `Validation error: ${msg.map(e => e.msg).join(', ')}` : 'Failed to create facility');
    } finally { setSaving(false); }
  };

  const handleCreateTrainer = async () => {
    if (!newTrainerLast.trim()) { toast.error('Please enter a last name'); return; }
    setSaving(true);
    try {
      const fullName = [newTrainerFirst.trim(), newTrainerLast.trim()].filter(Boolean).join(' ');
      const response = await crmAPI.create({ crm_class: 'contact', crm_type: 'person', system_name: fullName, person_first_name: newTrainerFirst.trim(), person_last_name: newTrainerLast.trim(), person_industry_role: 'trainer' });
      const newId = response.data?.id || response.id;
      toast.success('Trainer created');
      setCreateTrainerOpen(false); setNewTrainerFirst(''); setNewTrainerLast('');
      onEntitiesInvalidate?.();
      setForm(prev => ({ ...prev, boarding_location_id: newId }));
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(Array.isArray(msg) ? `Validation error: ${msg.map(e => e.msg).join(', ')}` : 'Failed to create trainer');
    } finally { setSaving(false); }
  };

  const getBoardingName = () => {
    if (horse.boarding_type === 'on_farm') return horse.farmfolio_location_name ? `On Farm (${horse.farmfolio_location_name})` : 'On Farm';
    if (horse.boarding_type === 'other') return horse.boarding_notes || 'Other';
    if (!horse.boarding_location_id) return null;
    return boardingFacilities.find(f => f.id === horse.boarding_location_id)?.system_name
        || trainerFacilities.find(t => t.id === horse.boarding_location_id)?.system_name
        || horse.boarding_location_id;
  };

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Boarding Location</CardTitle>
          {canEditAccount && !isEditing && (
            <Button variant="ghost" size="icon" onClick={startEditing} data-testid="edit-boarding-btn">
              <Pencil className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Location Type</label>
                <Select value={form.boarding_type} onValueChange={v => setF('boarding_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boarding_farm">Boarding Farm</SelectItem>
                    <SelectItem value="with_trainer">With Trainer</SelectItem>
                    {isFarm && <SelectItem value="on_farm">On Farm</SelectItem>}
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.boarding_type === 'boarding_farm' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Boarding Facility</label>
                    <Select value={form.boarding_location_id} onValueChange={v => setF('boarding_location_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Select boarding facility" /></SelectTrigger>
                      <SelectContent>
                        {boardingFacilities.map(f => <SelectItem key={f.id} value={f.id}>{f.system_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCreateBoardingOpen(true)} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Create New Boarding Facility
                  </Button>
                </>
              )}
              {form.boarding_type === 'with_trainer' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Trainer</label>
                    <Select value={form.boarding_location_id} onValueChange={v => setF('boarding_location_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Select trainer" /></SelectTrigger>
                      <SelectContent>
                        {trainerFacilities.map(t => <SelectItem key={t.id} value={t.id}>{t.system_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCreateTrainerOpen(true)} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Create New Trainer
                  </Button>
                </>
              )}
              {form.boarding_type === 'on_farm' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Location on Farm</label>
                  <Select
                    value={form.farmfolio_location_id || '__none__'}
                    onValueChange={v => {
                      const loc = farmfolioLocations.find(l => l.id === v);
                      setForm(prev => ({ ...prev, farmfolio_location_id: v === '__none__' ? '' : v, farmfolio_location_name: loc?.name || '' }));
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select location…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {farmfolioLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.boarding_type === 'other' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Location Notes</label>
                  <Input value={form.boarding_notes} onChange={e => setF('boarding_notes', e.target.value)} placeholder="e.g. Owner's property, Race track stabling" />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {getBoardingName() ? (
                <div className="flex flex-col gap-1">
                  {horse.boarding_type && (
                    <p className="text-xs text-muted-foreground capitalize">
                      {horse.boarding_type === 'with_trainer' ? 'With Trainer' : horse.boarding_type === 'on_farm' ? 'On Farm' : horse.boarding_type === 'other' ? 'Other' : 'Boarding Farm'}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    {horse.boarding_location_id && horse.boarding_type !== 'on_farm' && horse.boarding_type !== 'other' ? (
                      <button className="font-medium text-primary hover:underline" onClick={() => navigate(`/crm/accounts/${horse.boarding_location_id}`)}>
                        {getBoardingName()}
                      </button>
                    ) : (
                      <span className="font-medium">{getBoardingName()}</span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground italic">No boarding location specified</p>
              )}
            </div>
          )}
          {isEditing && (
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              {billingEntities.length > 0 && (
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">Default Invoice From</label>
                  <select
                    value={form.default_billing_entity_id || ''}
                    onChange={e => setF('default_billing_entity_id', e.target.value)}
                    className="h-8 w-full border rounded text-xs px-2 bg-white"
                  >
                    <option value="">Primary Organization</option>
                    {billingEntities.map(be => <option key={be.id} value={be.id}>{be.name}</option>)}
                  </select>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Boarding Facility */}
      <Dialog open={createBoardingOpen} onOpenChange={setCreateBoardingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create New Boarding Facility</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Facility Name</label>
              <Input value={newBoardingName} onChange={e => setNewBoardingName(e.target.value)} placeholder="Enter boarding facility name" autoFocus />
            </div>
            <p className="text-xs text-muted-foreground">This will create a new Boarding Farm Contact in your Virtual Rolodex.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateBoardingOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateBoarding} disabled={saving}>{saving ? 'Creating...' : 'Create Facility'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Trainer */}
      <Dialog open={createTrainerOpen} onOpenChange={v => { setCreateTrainerOpen(v); if (!v) { setNewTrainerFirst(''); setNewTrainerLast(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create New Trainer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name</label>
                <Input value={newTrainerFirst} onChange={e => setNewTrainerFirst(e.target.value)} placeholder="First name" autoFocus />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name *</label>
                <Input value={newTrainerLast} onChange={e => setNewTrainerLast(e.target.value)} placeholder="Last name" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">This will create a new Trainer Contact in your Virtual Rolodex.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTrainerOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTrainer} disabled={saving}>{saving ? 'Creating...' : 'Create Trainer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HorseBoardingCard;
